'use client';

import { MouseEventHandler } from 'react';
import { twJoin, twMerge } from 'tailwind-merge';
import Link from 'next/link';
import { LegacyIcon } from '../LegacyIcon';
import { EventClickElement } from '@tuic/tracking';
import {
  UnboundThirdPartyEventInvoker,
  useThirdPartyEvent,
} from '@tuic/lib/src/client';

export type FooterNavigationItem = {
  title: string;
  children: FooterSubNavigationItem[];
};

export type FooterSubNavigationItem = {
  title: string;
  href?: HTMLAnchorElement['href'];
  onClick?: MouseEventHandler<HTMLAnchorElement>;
  target?: HTMLAnchorElement['target'];
};

type FlyoutProps = {
  isActive: boolean;
  handleToggle: () => void;
  item: FooterNavigationItem;
};

const parseChildHref = (href?: string): string | undefined => {
  if (href?.substring(0, 7) === '#event:') {
    return undefined;
  }

  return href;
};

const createChildOnClickHandler = (
  invoke: UnboundThirdPartyEventInvoker,
  href: FooterSubNavigationItem['href'],
  onClick: FooterSubNavigationItem['onClick'],
): undefined | MouseEventHandler<HTMLAnchorElement> => {
  if (onClick) {
    return onClick;
  }

  if (href?.substring(0, 7) !== '#event:') {
    return undefined;
  }

  const eventName = href.substring(7);

  return ev => {
    ev.preventDefault();
    invoke(eventName);
  };
};

const Flyout = ({ isActive, handleToggle, item }: FlyoutProps) => {
  const { invoke } = useThirdPartyEvent();

  const children = item.children;

  return (
    <div
      data-track-click-category={item.title}
      className="cursor-pointer border-t-[1px] border-secondary-marine-100/50 lg:border-t-0 lg:border-none"
    >
      <a
        data-track-click-element={EventClickElement.Button}
        data-track-click-label={`mobile-flyout-${
          isActive ? 'expanded' : 'collapsed'
        }`}
        className={twMerge(
          twJoin(
            'flex flex-wrap items-center justify-start px-[1.875rem] py-[1.5625rem] text-[1.4375rem] lg:block lg:bg-transparent lg:px-0 lg:py-0',
            isActive && 'bg-white',
          ),
        )}
        role="button"
        onClick={handleToggle}
      >
        <h3 className="m-0 inline text-[1.4375rem] lg:block lg:text-[1.25rem] lg:font-[700] lg:leading-[1.875rem]">
          {item.title}
        </h3>
        <LegacyIcon
          className={twJoin(
            'my-0 ml-auto mr-0 lg:hidden',
            isActive && '-rotate-90',
            !isActive && 'rotate-90',
          )}
          name="chevron-right"
          size={30}
        />
      </a>

      <ul
        className={twMerge(
          twJoin(
            'hidden flex-col bg-white lg:mt-1 lg:block lg:bg-transparent space-y-4',
            isActive && 'flex lg:block',
          ),
        )}
      >
        {children.map(child => (
          <li
            className="cursor-pointer lg:leading-[1.3] [&+li]:border-t-[1px] [&+li]:border-secondary-marine-100/50 lg:[&+li]:mt-[0.6em] lg:[&+li]:border-none"
            key={child.title}
          >
            <Link
              data-track-click-element={EventClickElement.Link}
              data-track-click-label={child.title}
              className={twMerge(
                twJoin(
                  'flex lg:flex-nowrap flex-wrap items-center justify-start break-words px-[1.875rem] py-[1.5625rem] text-[1.4375rem] text-current lg:bg-transparent lg:px-0 lg:py-0 lg:text-[1rem] lg:hover:text-[#7390C5]',
                  isActive && 'bg-white',
                ),
              )}
              href={parseChildHref(child.href) ?? '#'}
              onClick={createChildOnClickHandler(
                invoke,
                child.href,
                child.onClick,
              )}
              target={child.target}
            >
              <LegacyIcon
                className={twJoin('hidden lg:inline mr-1')}
                name="chevron-right"
                size={16}
              />
              <span>{child.title}</span>
              <LegacyIcon
                className={twJoin('my-0 ml-auto mr-0 lg:hidden')}
                name="chevron-right"
                size={30}
              />
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default Flyout;
